import { useEffect, useState, type RefObject } from "react"

/**
 * Hook to track the width of a container element using ResizeObserver
 * Useful for responsive components that need to adapt based on available space
 *
 * @param ref - React ref to the container element
 * @returns The current width of the container in pixels, or null if not yet measured
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null)
 * const width = useContainerWidth(containerRef)
 *
 * return (
 *   <div ref={containerRef}>
 *     {width && width < 600 ? "Narrow" : "Wide"}
 *   </div>
 * )
 * ```
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    let observer: ResizeObserver | null = null
    let raf = 0
    let frames = 0
    const maxFrames = 90 // ~1.5s at 60fps - covers late Panel / tab mount

    const detach = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = 0
      observer?.disconnect()
      observer = null
    }

    const attach = () => {
      const el = ref.current
      if (!el) {
        // Ref often attaches after first paint (e.g. resizable Panel children).
        // Retry briefly instead of giving up with width=null forever.
        if (frames++ < maxFrames) {
          raf = requestAnimationFrame(attach)
        }
        return
      }

      setWidth(el.offsetWidth)
      observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setWidth(entry.contentRect.width)
        }
      })
      observer.observe(el)
    }

    attach()

    return detach
  }, [ref])

  return width
}
